import {NeoDatePicker, NeoSelect} from "@/components/forms";
import {EyeIcon} from "@/components/shared";
import {convertUtcDateRangeToLocalTimestamps} from "@/libs/helper";
import {NeoDashboardService} from "@/service/dashboard.service";
import {SelectItem} from "@nextui-org/react";
import {useEffect, useState} from "react";
import Loader from "../Loader";

interface FuelTheftRefillingProps {
  isLoading: boolean;
  usersetting: any;
  instituteuserids: any;
  fuelTheftANDRefilling: FuelSensorSummary;
  openPopup: (title: popupParams) => void;
}

interface FuelSensorSummary {
  fuelRefilling: {
    totalLitres: number;
    totalVehicles: number;
    affectedVehicles: number;
  };
  lowFuel: {
    totalVehicles: number;
    lowFuelVehicles: number;
    fuelLimit: number;
  };
  fuelDrop: {
    totalLitres: number;
    totalVehicles: number;
    affectedVehicles: number;
  };
}

type FuelTheftDay = "last7Days" | "thisWeek" | "lastWeek" | "custom";

const defaultFuelSensorSummary: FuelSensorSummary = {
  fuelRefilling: {
    totalLitres: 0,
    totalVehicles: 0,
    affectedVehicles: 0
  },
  lowFuel: {
    totalVehicles: 0,
    lowFuelVehicles: 0,
    fuelLimit: 0
  },
  fuelDrop: {
    totalLitres: 0,
    totalVehicles: 0,
    affectedVehicles: 0
  }
};

const getFuelTheftDateRange = (
  filter: FuelTheftDay,
  customRange: {from: Date | null; to: Date | null}
) => {
  const currentDate = new Date();

  if (filter === "last7Days") {
    const from = new Date(currentDate);
    from.setDate(currentDate.getDate() - 6);
    return {from, to: currentDate};
  }

  if (filter === "thisWeek" || filter === "lastWeek") {
    const dayOfWeek = currentDate.getDay();
    const diffToMonday = (dayOfWeek + 6) % 7;
    const weekStart = new Date(currentDate);
    weekStart.setDate(currentDate.getDate() - diffToMonday);

    if (filter === "thisWeek") {
      return {from: weekStart, to: currentDate};
    }

    const lastWeekFrom = new Date(weekStart);
    lastWeekFrom.setDate(weekStart.getDate() - 7);

    const lastWeekTo = new Date(weekStart);
    lastWeekTo.setDate(weekStart.getDate() - 1);

    return {from: lastWeekFrom, to: lastWeekTo};
  }

  if (!customRange.from || !customRange.to) {
    return null;
  }

  return {
    from: new Date(customRange.from),
    to: new Date(customRange.to)
  };
};

const fuelTheftDayFilter = [
  {
    value: "last7Days",
    label: "Last 7 Days"
  },
  {
    value: "thisWeek",
    label: "This Week"
  },
  {
    value: "lastWeek",
    label: "Last Week"
  },
  {
    value: "custom",
    label: "Custom"
  }
];
const FuelTheftRefillingwidget: React.FC<FuelTheftRefillingProps> = ({
  isLoading,
  usersetting,
  openPopup,
  fuelTheftANDRefilling,
  instituteuserids
}) => {
  const [fuelTheftDay, setFuelTheftDay] = useState<FuelTheftDay>("last7Days");
  const [fuelRefillingData, setfuelRefillingData] = useState<FuelSensorSummary>(
    defaultFuelSensorSummary
  );
  const [showDatePicker, SetShowDatePicker] = useState(false);
  const [customLoader, setCustomLoader] = useState(false);
  const [timeRange, setTimeRange] = useState<{
    from: Date | null;
    to: Date | null;
  }>({
    from: null,
    to: null
  });

  const onDateChange = (value: any) => {
    setTimeRange({
      from: value?.from ?? null,
      to: value?.to ?? null
    });
  };

  useEffect(() => {
    let isActive = true;

    (async () => {
      try {
        SetShowDatePicker(fuelTheftDay === "custom");

        if (Array.isArray(instituteuserids) && instituteuserids.length === 0) {
          return;
        }

        const selectedRange = getFuelTheftDateRange(fuelTheftDay, timeRange);
        if (!selectedRange) {
          return;
        }

        const customData = convertUtcDateRangeToLocalTimestamps(
          selectedRange.from,
          selectedRange.to
        );

        const customRequestData = {
          from: customData.from,
          to: customData.to,
          filters: {
            branch: instituteuserids
          }
        };

        setCustomLoader(true);

        const customresponse =
          await NeoDashboardService.onCustomFuelTheftRefilling(
            customRequestData
          );

        if (customresponse?.data?.status !== "success") {
          return;
        }

        const summary = customresponse?.data?.data?.fuelSensorSummary;

        if (summary && isActive) {
          setfuelRefillingData(summary);
        }
      } finally {
        if (isActive) {
          setCustomLoader(false);
        }
      }
    })();

    return () => {
      isActive = false;
    };
  }, [fuelTheftDay, instituteuserids, timeRange?.from, timeRange?.to]);

  const activeFuelData = fuelRefillingData ?? fuelTheftANDRefilling;

  const fuelSummary = activeFuelData;

  const fuelRefilling = fuelSummary?.fuelRefilling ?? {
    totalLitres: 0,
    totalVehicles: 0,
    affectedVehicles: 0
  };

  const lowFuel = fuelSummary?.lowFuel ?? {
    totalVehicles: 0,
    lowFuelVehicles: 0,
    fuelLimit: 0
  };

  const fuelDrop = fuelSummary?.fuelDrop ?? {
    totalLitres: 0,
    totalVehicles: 0,
    affectedVehicles: 0
  };

  const hasFuelTheft = fuelDrop?.affectedVehicles > 0;

  return (
    <div className="rounded-xl border bg-neo-white p-2">
      <div className=" mb-3 flex items-center  justify-between">
        <div className="flex items-center gap-1">
          <div className=" text-lg font-semibold">Fuel Theft & Refilling</div>
        </div>
        <div className="flex items-center">
          <h5
            className="cursor-pointer pr-2"
            title="Settings"
            onClick={usersetting}
          >
            <img
              className="h-6 w-6 p-1"
              src="/icons/setting_1.svg"
              alt="Setting"
            />
          </h5>
          <div>
            <h5
              className="cursor-pointer pr-2"
              title="View"
              //onClick={() => openPopup({title: "Fuel Filling & Theft"})}
              onClick={() => {
                const selectedRange = getFuelTheftDateRange(
                  fuelTheftDay,
                  timeRange
                );

                if (!selectedRange) return;

                const customData = convertUtcDateRangeToLocalTimestamps(
                  selectedRange.from,
                  selectedRange.to
                );

                openPopup({
                  title: "Fuel Filling & Theft",
                  from: customData.from,
                  to: customData.to
                });
              }}
            >
              <EyeIcon size={18} />
            </h5>
          </div>
        </div>
      </div>
      {/* heading */}

      <div className="flex flex-col gap-2">
        {isLoading || customLoader ? (
          <Loader />
        ) : (
          <>
            <div className="flex items-center justify-between gap-3 rounded-2xl border  px-1 py-2">
              <NeoSelect
                aria-label="Fuel theft filter"
                size="sm"
                disallowEmptySelection
                selectedKeys={[fuelTheftDay]}
                startContent={
                  <svg
                    width="15"
                    height="16"
                    viewBox="0 0 21 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M18.5725 1.68841H16.0399V0.844207C16.0399 0.620309 15.951 0.405582 15.7927 0.247262C15.6343 0.0889429 15.4196 0 15.1957 0C14.9718 0 14.7571 0.0889429 14.5988 0.247262C14.4405 0.405582 14.3515 0.620309 14.3515 0.844207V1.68841H5.90945V0.844207C5.90945 0.620309 5.8205 0.405582 5.66219 0.247262C5.50387 0.0889429 5.28914 0 5.06524 0C4.84134 0 4.62662 0.0889429 4.4683 0.247262C4.30998 0.405582 4.22103 0.620309 4.22103 0.844207V1.68841H1.68841C1.24062 1.68841 0.811164 1.8663 0.494525 2.18294C0.177886 2.49958 0 2.92903 0 3.37683V20.261C0 20.7088 0.177886 21.1382 0.494525 21.4549C0.811164 21.7715 1.24062 21.9494 1.68841 21.9494H18.5725C19.0203 21.9494 19.4498 21.7715 19.7664 21.4549C20.0831 21.1382 20.261 20.7088 20.261 20.261V3.37683C20.261 2.92903 20.0831 2.49958 19.7664 2.18294C19.4498 1.8663 19.0203 1.68841 18.5725 1.68841ZM5.48734 17.7283C5.23689 17.7283 4.99206 17.6541 4.78382 17.5149C4.57558 17.3758 4.41327 17.178 4.31743 16.9466C4.22158 16.7152 4.19651 16.4606 4.24537 16.215C4.29423 15.9693 4.41483 15.7437 4.59193 15.5666C4.76902 15.3895 4.99466 15.2689 5.2403 15.2201C5.48594 15.1712 5.74055 15.1963 5.97194 15.2921C6.20333 15.388 6.4011 15.5503 6.54024 15.7585C6.67939 15.9668 6.75365 16.2116 6.75365 16.462C6.75365 16.7979 6.62024 17.12 6.38276 17.3574C6.14528 17.5949 5.82319 17.7283 5.48734 17.7283ZM10.1305 17.7283C9.88003 17.7283 9.6352 17.6541 9.42696 17.5149C9.21871 17.3758 9.05641 17.178 8.96056 16.9466C8.86472 16.7152 8.83964 16.4606 8.8885 16.215C8.93736 15.9693 9.05797 15.7437 9.23507 15.5666C9.41216 15.3895 9.6378 15.2689 9.88344 15.2201C10.1291 15.1712 10.3837 15.1963 10.6151 15.2921C10.8465 15.388 11.0442 15.5503 11.1834 15.7585C11.3225 15.9668 11.3968 16.2116 11.3968 16.462C11.3968 16.7979 11.2634 17.12 11.0259 17.3574C10.7884 17.5949 10.4663 17.7283 10.1305 17.7283ZM10.1305 13.5073C9.88003 13.5073 9.6352 13.433 9.42696 13.2939C9.21871 13.1548 9.05641 12.957 8.96056 12.7256C8.86472 12.4942 8.83964 12.2396 8.8885 11.994C8.93736 11.7483 9.05797 11.5227 9.23507 11.3456C9.41216 11.1685 9.6378 11.0479 9.88344 10.999C10.1291 10.9502 10.3837 10.9752 10.6151 11.0711C10.8465 11.1669 11.0442 11.3292 11.1834 11.5375C11.3225 11.7457 11.3968 11.9905 11.3968 12.241C11.3968 12.5768 11.2634 12.8989 11.0259 13.1364C10.7884 13.3739 10.4663 13.5073 10.1305 13.5073ZM14.7736 17.7283C14.5232 17.7283 14.2783 17.6541 14.0701 17.5149C13.8619 17.3758 13.6995 17.178 13.6037 16.9466C13.5079 16.7152 13.4828 16.4606 13.5316 16.215C13.5805 15.9693 13.7011 15.7437 13.8782 15.5666C14.0553 15.3895 14.2809 15.2689 14.5266 15.2201C14.7722 15.1712 15.0268 15.1963 15.2582 15.2921C15.4896 15.388 15.6874 15.5503 15.8265 15.7585C15.9657 15.9668 16.0399 16.2116 16.0399 16.462C16.0399 16.7979 15.9065 17.12 15.669 17.3574C15.4316 17.5949 15.1095 17.7283 14.7736 17.7283ZM14.7736 13.5073C14.5232 13.5073 14.2783 13.433 14.0701 13.2939C13.8619 13.1548 13.6995 12.957 13.6037 12.7256C13.5079 12.4942 13.4828 12.2396 13.5316 11.994C13.5805 11.7483 13.7011 11.5227 13.8782 11.3456C14.0553 11.1685 14.2809 11.0479 14.5266 10.999C14.7722 10.9502 15.0268 10.9752 15.2582 11.0711C15.4896 11.1669 15.6874 11.3292 15.8265 11.5375C15.9657 11.7457 16.0399 11.9905 16.0399 12.241C16.0399 12.5768 15.9065 12.8989 15.669 13.1364C15.4316 13.3739 15.1095 13.5073 14.7736 13.5073ZM18.5725 6.75365H1.68841V3.37683H4.22103V4.22103C4.22103 4.44493 4.30998 4.65966 4.4683 4.81798C4.62662 4.9763 4.84134 5.06524 5.06524 5.06524C5.28914 5.06524 5.50387 4.9763 5.66219 4.81798C5.8205 4.65966 5.90945 4.44493 5.90945 4.22103V3.37683H14.3515V4.22103C14.3515 4.44493 14.4405 4.65966 14.5988 4.81798C14.7571 4.9763 14.9718 5.06524 15.1957 5.06524C15.4196 5.06524 15.6343 4.9763 15.7927 4.81798C15.951 4.65966 16.0399 4.44493 16.0399 4.22103V3.37683H18.5725V6.75365Z"
                      fill="#293050"
                    />
                  </svg>
                }
                onSelectionChange={keys => {
                  const value = Array.from(keys)[0];

                  if (!value) return;

                  const selectedValue = String(value) as FuelTheftDay;
                  setFuelTheftDay(selectedValue);

                  if (selectedValue === "custom") {
                    SetShowDatePicker(true);
                  } else {
                    SetShowDatePicker(false);
                    setTimeRange({
                      from: null,
                      to: null
                    });
                  }
                }}
              >
                {fuelTheftDayFilter.map(item => (
                  <SelectItem key={item.value}>{item.label}</SelectItem>
                ))}
              </NeoSelect>
              {showDatePicker && (
                <NeoDatePicker
                  type="range"
                  label="Date Range"
                  rangedefaultValue={
                    timeRange.from && timeRange.to ? timeRange : undefined
                  }
                  handleDateChange={onDateChange}
                />
              )}
            </div>
            {/* 2 */}
            <div className="flex w-full gap-2 ">
              {/* 11 */}
              <div className="flex w-[50%] flex-col items-center justify-center gap-1 rounded-lg border border-gray-200 p-4 ">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#2563EB33]">
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M39.194 19.5971C39.194 24.9963 36.9943 29.9159 33.4356 33.4746C29.9159 36.9944 24.9962 39.1941 19.597 39.1941C17.438 39.1941 15.3568 38.8348 13.3571 38.1943C11.5981 37.5943 9.95767 36.7538 8.39871 35.6743L9.07829 38.074C9.15796 38.3536 8.99861 38.6739 8.71897 38.7145C8.43932 38.7942 8.15969 38.6348 8.07843 38.3552L7.07858 34.6761C6.9989 34.3964 7.15826 34.0761 7.4379 34.0355L11.117 33.0357C11.3967 32.956 11.6763 33.1153 11.7576 33.395C11.8372 33.6746 11.6779 33.9543 11.3982 34.0355L8.95797 34.7151C10.4374 35.7556 12.0372 36.5555 13.7572 37.1554C15.6366 37.7959 17.5973 38.1146 19.6359 38.1146C24.7555 38.1146 29.3936 36.0352 32.7543 32.6746C36.1131 29.3158 38.1943 24.6758 38.1943 19.5562C38.1943 17.4768 37.874 15.516 37.235 13.6775C36.5945 11.7575 35.6353 9.99838 34.4355 8.3987C34.2761 8.15811 34.3152 7.83942 34.5558 7.63946C34.7964 7.4801 35.115 7.51916 35.315 7.75975C36.5945 9.43917 37.5943 11.3186 38.2739 13.3589C38.8348 15.318 39.194 17.4378 39.194 19.5971ZM19.597 1.07949C21.4764 1.07949 23.2371 1.35914 24.9554 1.87937C26.7144 2.39961 28.3548 3.19949 29.8342 4.1587C29.8748 4.1587 29.8748 4.19932 29.9139 4.19932L27.3143 4.91953C27.0346 4.99921 26.8737 5.27885 26.955 5.56008C27.0346 5.83972 27.3143 6.00065 27.5955 5.9194L31.2746 4.91955C31.5543 4.83987 31.7152 4.56023 31.6339 4.27901L30.6341 0.599885C30.5544 0.320239 30.2748 0.159312 29.9935 0.240563C29.7139 0.320239 29.553 0.599885 29.6342 0.881106L30.2341 3.08077C28.714 2.12156 27.0753 1.32168 25.2757 0.801438C23.476 0.279638 21.556 0 19.597 0C14.1978 0 9.27817 2.19967 5.75831 5.75833C2.24009 9.3187 0 14.1978 0 19.597C0 21.6764 0.320263 23.676 0.920178 25.5961C1.56072 27.5552 2.4793 29.3549 3.64008 30.9953C3.71975 31.075 3.76037 31.1546 3.79943 31.1953C3.87911 31.2749 3.91972 31.3546 3.95878 31.3952C4.11813 31.6358 4.47902 31.6749 4.71802 31.5155C4.95861 31.3562 4.99767 30.9953 4.83832 30.7563C4.75864 30.6766 4.71802 30.5969 4.67897 30.5563C4.63835 30.4766 4.55867 30.397 4.51961 30.3563C3.39947 28.7972 2.51992 27.0772 1.96065 25.2367C1.39982 23.4761 1.07955 21.556 1.07955 19.5968C1.07955 14.4772 3.15891 9.83906 6.51951 6.51916C9.83931 3.15875 14.4774 1.07949 19.597 1.07949ZM26.3959 20.5969C26.8365 21.7171 27.0365 22.8763 27.0365 23.9558C27.0365 25.4759 26.6365 26.9147 25.8773 28.1143C25.1181 29.2735 23.9979 30.2343 22.5981 30.7546C21.7186 31.0748 20.7578 31.2748 19.6783 31.2748C18.7581 31.2748 17.8785 31.1155 17.1193 30.8749C15.6398 30.3953 14.3994 29.4345 13.5605 28.2346C12.72 27.0348 12.2404 25.5554 12.2404 23.9557C12.2404 23.0762 12.3997 22.1966 12.6809 21.2764C13.8011 17.9176 16.4007 13.798 18.4004 10.6372C18.8801 9.878 19.3206 9.15778 19.7206 8.55788C20.1205 9.19842 20.5611 9.878 21 10.6372C22.7966 13.5571 25.1558 17.3566 26.396 20.5965L26.3959 20.5969ZM24.7555 23.6761C24.4353 23.6355 24.1963 23.8761 24.1556 24.1557C24.076 24.876 23.876 25.5555 23.5151 26.1961C23.1558 26.8366 22.6746 27.3959 21.995 27.9161C21.7544 28.1161 21.7154 28.4364 21.9153 28.6754C22.1153 28.916 22.4356 28.955 22.6746 28.7551C23.4338 28.1551 24.0337 27.4756 24.4337 26.7147C24.8742 25.9555 25.1132 25.1556 25.2335 24.2745C25.2757 23.9558 25.0758 23.7168 24.7555 23.6761Z"
                      fill="#2563EB"
                    />
                  </svg>
                </div>
                <div className="text-lg font-bold">
                  {Number(fuelRefilling?.totalLitres ?? 0).toFixed(2)}
                  <span className="text-xs text-gray-500"> Litres</span>
                </div>
                <div className="text-gray-600">Fuel Refilling</div>
                <div className="flex h-8 w-32 flex-row items-center justify-center gap-3 rounded-xl bg-[#F2F6FF] p-1">
                  <div>
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9.55007 0C14.5639 0 17.9064 1.31313 17.9064 2.9844V3.58127V4.77503C18.5667 4.77503 19.1001 5.30849 19.1001 5.96879V8.35631C19.1001 9.01661 18.5667 9.55007 17.9064 9.55007V15.5189C17.9064 16.1792 17.3729 16.7126 16.7126 16.7126V17.9064C16.7126 18.5667 16.1792 19.1001 15.5189 19.1001H14.3251C13.6648 19.1001 13.1313 18.5667 13.1313 17.9064V16.7126H5.96879V17.9064C5.96879 18.5667 5.43533 19.1001 4.77503 19.1001H3.58127C2.92098 19.1001 2.38752 18.5667 2.38752 17.9064V16.7126C1.72722 16.7126 1.19376 16.1792 1.19376 15.5189V9.55007C0.533461 9.55007 0 9.01661 0 8.35631V5.96879C0 5.30849 0.533461 4.77503 1.19376 4.77503V3.58127V2.9844C1.19376 1.31313 4.53628 0 9.55007 0ZM3.58127 5.96879V9.55007C3.58127 10.2104 4.11474 10.7438 4.77503 10.7438H8.95319V4.77503H4.77503C4.11474 4.77503 3.58127 5.30849 3.58127 5.96879ZM10.1469 10.7438H14.3251C14.9854 10.7438 15.5189 10.2104 15.5189 9.55007V5.96879C15.5189 5.30849 14.9854 4.77503 14.3251 4.77503H10.1469V10.7438ZM4.17815 14.922C4.49476 14.922 4.7984 14.7962 5.02227 14.5723C5.24614 14.3485 5.37191 14.0448 5.37191 13.7282C5.37191 13.4116 5.24614 13.108 5.02227 12.8841C4.7984 12.6602 4.49476 12.5345 4.17815 12.5345C3.86155 12.5345 3.55791 12.6602 3.33404 12.8841C3.11017 13.108 2.9844 13.4116 2.9844 13.7282C2.9844 14.0448 3.11017 14.3485 3.33404 14.5723C3.55791 14.7962 3.86155 14.922 4.17815 14.922ZM14.922 14.922C15.2386 14.922 15.5422 14.7962 15.7661 14.5723C15.99 14.3485 16.1157 14.0448 16.1157 13.7282C16.1157 13.4116 15.99 13.108 15.7661 12.8841C15.5422 12.6602 15.2386 12.5345 14.922 12.5345C14.6054 12.5345 14.3017 12.6602 14.0779 12.8841C13.854 13.108 13.7282 13.4116 13.7282 13.7282C13.7282 14.0448 13.854 14.3485 14.0779 14.5723C14.3017 14.7962 14.6054 14.922 14.922 14.922ZM13.1313 2.9844C13.1313 2.65611 12.8627 2.38752 12.5345 2.38752H6.56567C6.23739 2.38752 5.96879 2.65611 5.96879 2.9844C5.96879 3.31268 6.23739 3.58127 6.56567 3.58127H12.5345C12.8627 3.58127 13.1313 3.31268 13.1313 2.9844Z"
                        fill="#A3AED0"
                      />
                    </svg>
                  </div>
                  <div className="text-xs font-medium text-gray-600">
                    {fuelRefilling?.affectedVehicles}/
                    {fuelRefilling?.totalVehicles} vehicles
                  </div>
                </div>
              </div>
              {/* 22 */}
              <div className="flex w-[50%] flex-col items-center justify-center gap-1 rounded-lg border border-gray-200 p-4 ">
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#FFC72733]">
                  <svg
                    width="25"
                    height="23"
                    viewBox="0 0 32 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M31.7286 12.4135C31.7286 12.2663 31.6913 12.1211 31.621 11.9923L26.2035 2.05409C25.9694 1.62383 25.428 1.46427 24.9925 1.69601C24.5586 1.92899 24.3973 2.46671 24.6314 2.89697L26.0054 5.41781L24.1959 8.55185C24.073 8.76432 24.0441 9.01657 24.116 9.25036C24.188 9.48415 24.3535 9.67815 24.5747 9.78644L29.7792 12.3401L29.9418 12.6383V19.9158C29.9418 20.9916 29.0595 21.8665 27.9754 21.8665H27.0345C25.9504 21.8665 25.069 20.9916 25.069 19.9158V15.1924C25.069 13.6407 23.7959 12.3782 22.2312 12.3782H20.906V3.13937C20.906 1.40726 19.4865 0 17.741 0H6.07306C4.32638 0 2.90728 1.40767 2.90728 3.13937V28.0903H0.893401C0.399963 28.0903 0 28.4877 0 28.9762C0 29.4647 0.399963 29.8622 0.893401 29.8622H22.4885C22.9811 29.8622 23.3811 29.4647 23.3811 28.9762C23.3811 28.4877 22.9811 28.0903 22.4885 28.0903H20.906V14.1501H22.2308C22.8099 14.1501 23.2818 14.6181 23.2818 15.1924V19.9158C23.2818 21.969 24.9652 23.6384 27.0341 23.6384H27.975C30.0447 23.6384 31.7281 21.969 31.7281 19.9158L31.7286 12.4135ZM26.218 8.61584L27.0055 7.25164L28.3076 9.64124L26.218 8.61584ZM13.8775 22.8123C13.3572 23.4021 12.6561 23.7266 11.9062 23.7266C10.3924 23.7266 9.15943 22.3743 9.15943 20.7135C9.16026 19.2571 11.0132 16.8273 11.3842 16.3556C11.6345 16.0385 12.1767 16.0352 12.4278 16.3556C12.7992 16.8273 14.6526 19.2579 14.6526 20.7135C14.653 21.501 14.3784 22.2471 13.8775 22.8123ZM16.4878 10.1593C16.4878 11.7638 15.1717 13.0698 13.5545 13.0698H9.68017C8.06294 13.0698 6.74601 11.7638 6.74601 10.1593V6.88906C6.74601 5.28451 8.06294 3.97856 9.68017 3.97856H13.5545C15.1717 3.97856 16.4878 5.28451 16.4878 6.88906V10.1593Z"
                      fill="#F28A03"
                    />
                  </svg>
                </div>
                <div className="text-lg font-bold">
                  {lowFuel?.lowFuelVehicles}/{lowFuel?.totalVehicles}
                </div>
                <div className="text-gray-600">Low Fuel Vehicles</div>
                <div className="w-30 flex h-8 flex-row items-center justify-center gap-2 rounded-xl bg-[#F28A0333] p-1 px-3">
                  <div className="h-2 w-2 rounded-full bg-[#F28A03]"></div>
                  <div className="text-xs font-medium text-[#F28A03]">
                    Limit {lowFuel?.fuelLimit} %
                  </div>
                </div>
              </div>
            </div>
            {/* 2 */}
            {/* 3 */}
            {!hasFuelTheft ? (
              <div className="flex items-center gap-10 rounded-lg border border-gray-200 p-2">
                <div className="flex flex-row items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#349C4233]">
                    <svg
                      width="25"
                      height="27"
                      viewBox="0 0 30 32"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M14.9311 0C15.2173 0 15.5034 0.0623106 15.7647 0.180701L27.4794 5.15932C28.8481 5.73881 29.8684 7.09095 29.8621 8.72348C29.831 14.9047 27.2928 26.2141 16.5735 31.3547C15.5345 31.8532 14.3276 31.8532 13.2887 31.3547C2.56941 26.2141 0.0311347 14.9047 2.82974e-05 8.72348C-0.00619298 7.09095 1.0141 5.73881 2.38278 5.15932L14.1037 0.180701C14.3587 0.0623106 14.6449 0 14.9311 0ZM14.9311 4.16235V27.7158C23.5164 23.5534 25.8245 14.3377 25.8805 8.81072L14.9311 4.16235Z"
                        fill="#15803D"
                      />
                    </svg>
                  </div>
                  <div className="flex flex-col gap-1 text-gray-500">
                    <div>Fuel Theft Status</div>
                    <div className="text-xs">
                      No fuel theft detect during this period
                    </div>
                  </div>
                </div>
                <div className="flex h-6 w-24 flex-row items-center justify-center gap-1 rounded-lg bg-[#349C4233] p-1">
                  <div className="h-2 w-2 rounded-full bg-[#349C42]"></div>
                  <div className="text-xs text-[#349C42]">Fuel Secure</div>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-10 rounded-lg border border-gray-200 p-2">
                <div className="flex flex-row items-center gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#FC2A3D33] p-1">
                    <svg
                      width="25"
                      height="24"
                      viewBox="0 0 37 33"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M36.4713 27.0747L22.0272 2.01639C21.6663 1.40247 21.151 0.893441 20.5324 0.539753C19.9139 0.186065 19.2135 0 18.5008 0C17.7881 0 17.0878 0.186065 16.4692 0.539753C15.8507 0.893441 15.3354 1.40247 14.9744 2.01639L0.530316 27.0747C0.183023 27.6685 0 28.3439 0 29.0316C0 29.7193 0.183023 30.3947 0.530316 30.9885C0.886638 31.6061 1.40104 32.1179 2.02075 32.4713C2.64045 32.8248 3.34314 33.0072 4.0567 32.9998H32.945C33.658 33.0066 34.36 32.8239 34.979 32.4705C35.5981 32.1171 36.112 31.6056 36.468 30.9885C36.8158 30.3949 36.9994 29.7197 37 29.032C37.0006 28.3443 36.8181 27.6688 36.4713 27.0747ZM34.1804 29.6668C34.0545 29.8814 33.8737 30.0587 33.6566 30.1804C33.4395 30.3022 33.1939 30.3641 32.945 30.3598H4.0567C3.80775 30.3641 3.56213 30.3022 3.34503 30.1804C3.12793 30.0587 2.94715 29.8814 2.82123 29.6668C2.70717 29.4739 2.647 29.254 2.647 29.0299C2.647 28.8059 2.70717 28.5859 2.82123 28.393L17.2654 3.33473C17.3938 3.12117 17.5754 2.94446 17.7925 2.82178C18.0096 2.6991 18.2547 2.63463 18.5041 2.63463C18.7535 2.63463 18.9987 2.6991 19.2158 2.82178C19.4328 2.94446 19.6144 3.12117 19.7429 3.33473L34.187 28.393C34.3001 28.5865 34.3591 28.8068 34.358 29.0308C34.3568 29.2549 34.2955 29.4745 34.1804 29.6668ZM17.1795 19.7999V13.2C17.1795 12.8499 17.3187 12.5142 17.5665 12.2666C17.8143 12.0191 18.1504 11.88 18.5008 11.88C18.8513 11.88 19.1874 12.0191 19.4352 12.2666C19.683 12.5142 19.8222 12.8499 19.8222 13.2V19.7999C19.8222 20.15 19.683 20.4857 19.4352 20.7333C19.1874 20.9808 18.8513 21.1199 18.5008 21.1199C18.1504 21.1199 17.8143 20.9808 17.5665 20.7333C17.3187 20.4857 17.1795 20.15 17.1795 19.7999ZM20.4829 25.7399C20.4829 26.1315 20.3666 26.5143 20.1488 26.8399C19.931 27.1655 19.6215 27.4193 19.2593 27.5691C18.8971 27.719 18.4986 27.7582 18.1141 27.6818C17.7297 27.6054 17.3765 27.4168 17.0993 27.1399C16.8221 26.863 16.6333 26.5102 16.5569 26.1261C16.4804 25.7421 16.5196 25.3439 16.6697 24.9822C16.8197 24.6204 17.0737 24.3111 17.3997 24.0936C17.7256 23.876 18.1088 23.7599 18.5008 23.7599C19.0265 23.7599 19.5306 23.9685 19.9023 24.3398C20.274 24.7111 20.4829 25.2147 20.4829 25.7399Z"
                        fill="#FC2A3D"
                      />
                    </svg>
                  </div>
                  <div className="flex flex-col gap-1 text-gray-500">
                    <div>Unusual fuel drop detected!</div>
                    <div className="text-xs">
                      {fuelDrop?.affectedVehicles}/{fuelDrop?.totalVehicles}{" "}
                      Vehicles
                    </div>
                  </div>
                </div>
                <div className="flex h-6 w-24 flex-row items-center justify-center gap-2 rounded-lg bg-[#FC2A3D33] p-1">
                  <div className="h-2 w-2 rounded-full bg-[#FC2A3D]"></div>
                  <div className="text-xs text-[#FC2A3D]">Fuel Theft</div>
                </div>
              </div>
            )}
            {/* 3 */}
          </>
        )}
      </div>
    </div>
  );
};

export default FuelTheftRefillingwidget;
